/**
 * Country panel — displays country info, CII scores, event summary by type,
 * recent events, and active layers with data in the selected country.
 * Triggered by country:selected on the event bus.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import type { NormalizedEvent } from '@/sources/base-source';
import { createCompactEventItem } from '@/ui/components/compact-event-item';
import { getLayerById } from '@/config/layers';
import { TYPE_TO_LAYER } from '@/map/layers/index';
import type { CIIScore } from '@/intelligence/analysis/types';
import { getCIIClassification, getCIIColor } from '@/intelligence/analysis/cii';
import { getAnalysisEngine } from '@/intelligence/analysis/engine';
import { filterEventsByTimeRange } from '@/utils/time-filter';
import { createTrendSparkline } from '@/ui/components/sparkline';

/** Max CII history data points per country (for sparkline rendering). */
const MAX_CII_HISTORY = 20;

export class CountryPanel {
  private container: HTMLElement;
  private contentArea: HTMLElement;
  private currentCountry: { name: string; code: string } | null = null;
  private cleanupFns: (() => void)[] = [];
  private renderPending = false;
  /** Session-local CII overall score history per country code (for sparklines). */
  private ciiHistory: Map<string, number[]> = new Map();

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'country-panel';
    this.container.style.cssText = 'display: flex; flex-direction: column; height: 100%; overflow-y: auto;';

    this.contentArea = document.createElement('div');
    this.contentArea.style.cssText = 'padding: 12px;';
    this.container.appendChild(this.contentArea);

    this.renderEmpty();

    // Listen for country selection
    this.cleanupFns.push(
      eventBus.on('country:selected', (country) => {
        this.currentCountry = country;
        this.render(country);
      }),
    );

    // Re-render when events change (new data might affect counts)
    this.cleanupFns.push(
      appStore.subscribe(
        (s) => s.events,
        () => this.scheduleRender(),
      ),
    );

    // Refresh CII when new analysis results arrive + record history
    this.cleanupFns.push(
      eventBus.on('analysis:complete', (result) => {
        // Record CII history for sparklines
        if (result.ciiScores) {
          for (const [code, score] of result.ciiScores) {
            let history = this.ciiHistory.get(code);
            if (!history) {
              history = [];
              this.ciiHistory.set(code, history);
            }
            history.push(score.overall);
            if (history.length > MAX_CII_HISTORY) {
              history.shift();
            }
          }
        }
        this.scheduleRender();
      }),
    );

    // Re-render when time range changes
    this.cleanupFns.push(
      appStore.subscribe(
        (s) => s.timeRange,
        () => this.scheduleRender(),
      ),
    );
  }

  getElement(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    for (const fn of this.cleanupFns) fn();
    this.cleanupFns = [];
  }

  /** Debounced re-render via requestAnimationFrame. */
  private scheduleRender(): void {
    if (!this.currentCountry || this.renderPending) return;
    this.renderPending = true;
    requestAnimationFrame(() => {
      this.renderPending = false;
      if (this.currentCountry) {
        this.render(this.currentCountry);
      }
    });
  }

  /** Render empty state. */
  private renderEmpty(): void {
    this.contentArea.textContent = '';
    const msg = document.createElement('div');
    msg.style.cssText = 'padding: 32px 16px; text-align: center; color: var(--text-muted); font-size: 12px;';
    msg.textContent = 'Click the map to view country information';
    this.contentArea.appendChild(msg);
  }

  /** Render country information. */
  private render(country: { name: string; code: string }): void {
    this.contentArea.textContent = '';

    // --- Header: Country name + code ---
    const header = document.createElement('div');
    header.style.cssText = 'margin-bottom: 12px;';

    const titleRow = document.createElement('div');
    titleRow.style.cssText = 'display: flex; align-items: center; justify-content: space-between;';

    const title = document.createElement('h3');
    title.style.cssText = 'margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary);';
    title.textContent = `${country.name} (${country.code})`;
    titleRow.appendChild(title);

    const dossierBtn = document.createElement('button');
    dossierBtn.style.cssText = `
      font-size: 10px; padding: 3px 8px; border-radius: 4px; cursor: pointer;
      background: var(--accent-bg); color: var(--accent); border: 1px solid var(--accent-border);
      font-family: inherit; font-weight: 600; letter-spacing: 0.03em;
    `;
    dossierBtn.textContent = 'Full Dossier';
    dossierBtn.title = 'Open full country intelligence dossier';
    dossierBtn.onclick = () => eventBus.emit('country:dossier', country);
    titleRow.appendChild(dossierBtn);

    header.appendChild(titleRow);

    this.contentArea.appendChild(header);

    // --- CII Section ---
    this.contentArea.appendChild(this.renderCII(country.code));

    // --- Event summary by type ---
    const state = appStore.getState();
    const events = filterEventsByTimeRange(state.events, state.timeRange);
    const countryEvents = this.getCountryEvents(events, country.code, country.name);

    const summarySection = document.createElement('div');
    summarySection.style.cssText = 'margin-bottom: 16px;';

    const summaryHeader = document.createElement('div');
    summaryHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    summaryHeader.textContent = `Event Summary (${countryEvents.length})`;
    summarySection.appendChild(summaryHeader);

    if (countryEvents.length === 0) {
      const noData = document.createElement('div');
      noData.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      noData.textContent = 'No events recorded for this country';
      summarySection.appendChild(noData);
    } else {
      // Count by type
      const typeCounts = new Map<string, number>();
      for (const ev of countryEvents) {
        const t = ev.type.replace(/_/g, ' ');
        typeCounts.set(t, (typeCounts.get(t) ?? 0) + 1);
      }

      // Sort by count descending
      const sorted = Array.from(typeCounts.entries()).sort((a, b) => b[1] - a[1]);

      const list = document.createElement('div');
      list.style.cssText = 'display: flex; flex-direction: column; gap: 2px;';

      for (const [type, count] of sorted) {
        const row = document.createElement('div');
        row.style.cssText = `
          display: flex; justify-content: space-between; align-items: center;
          font-size: 12px; padding: 3px 8px; border-radius: 4px;
          background: var(--bg-secondary);
        `;

        const typeLabel = document.createElement('span');
        typeLabel.style.color = 'var(--text-primary)';
        typeLabel.textContent = type;
        row.appendChild(typeLabel);

        const countLabel = document.createElement('span');
        countLabel.style.cssText = 'font-weight: 600; color: var(--text-secondary); font-size: 11px;';
        countLabel.textContent = String(count);
        row.appendChild(countLabel);

        list.appendChild(row);
      }

      summarySection.appendChild(list);
    }

    this.contentArea.appendChild(summarySection);

    // --- Recent events (last 20) ---
    const recentSection = document.createElement('div');
    recentSection.style.cssText = 'margin-bottom: 16px;';

    const recentHeader = document.createElement('div');
    recentHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    recentHeader.textContent = 'Recent Events';
    recentSection.appendChild(recentHeader);

    if (countryEvents.length === 0) {
      const noRecent = document.createElement('div');
      noRecent.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      noRecent.textContent = 'No recent events';
      recentSection.appendChild(noRecent);
    } else {
      // Sort newest first, take 20
      const recent = countryEvents
        .slice()
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        .slice(0, 20);

      for (const ev of recent) {
        recentSection.appendChild(createCompactEventItem(ev));
      }
    }

    this.contentArea.appendChild(recentSection);

    // --- Active layers with data in this country ---
    const layersSection = document.createElement('div');

    const layersHeader = document.createElement('div');
    layersHeader.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    layersHeader.textContent = 'Active Layers';
    layersSection.appendChild(layersHeader);

    const activeLayers = appStore.getState().activeLayers;
    const layersWithData = this.getLayersWithData(countryEvents, activeLayers);

    if (layersWithData.length === 0) {
      const noLayers = document.createElement('div');
      noLayers.style.cssText = 'font-size: 11px; color: var(--text-muted); font-style: italic;';
      noLayers.textContent = 'No active layers with data in this country';
      layersSection.appendChild(noLayers);
    } else {
      const layerList = document.createElement('div');
      layerList.style.cssText = 'display: flex; flex-wrap: wrap; gap: 4px;';

      for (const layerName of layersWithData) {
        const chip = document.createElement('span');
        chip.style.cssText = `
          font-size: 10px; padding: 2px 8px; border-radius: 10px;
          background: var(--bg-secondary); color: var(--text-secondary);
          border: 1px solid var(--border-primary);
        `;
        chip.textContent = layerName;
        layerList.appendChild(chip);
      }

      layersSection.appendChild(layerList);
    }

    this.contentArea.appendChild(layersSection);
  }

  /** Render the CII section for a country. */
  private renderCII(countryCode: string): HTMLElement {
    const section = document.createElement('div');
    section.style.cssText = 'margin-bottom: 16px;';

    const header = document.createElement('div');
    header.style.cssText = 'font-size: 11px; font-weight: 600; color: var(--text-secondary); margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.05em;';
    header.textContent = 'Country Instability Index';
    section.appendChild(header);

    // Get CII score from the analysis engine
    const engine = getAnalysisEngine();
    const lastResult = engine?.getLastResult();
    const ciiScore: CIIScore | undefined = lastResult?.ciiScores.get(countryCode.toUpperCase());

    if (!ciiScore) {
      // No CII data yet — show warmup state
      const placeholder = document.createElement('div');
      placeholder.style.cssText = `
        padding: 12px; border-radius: 6px;
        background: var(--bg-secondary); border: 1px dashed var(--border-primary);
        font-size: 11px; color: var(--text-muted); text-align: center;
      `;
      const isWarmingUp = engine && !engine.isCIIActive();
      placeholder.textContent = isWarmingUp
        ? 'CII warming up (15-min baseline collection)'
        : 'CII not available for this country';
      section.appendChild(placeholder);
      return section;
    }

    const card = document.createElement('div');
    card.style.cssText = `
      padding: 12px; border-radius: 6px;
      background: var(--bg-secondary); border: 1px solid var(--border-primary);
    `;

    // Overall score row: score number + classification + trend
    const scoreRow = document.createElement('div');
    scoreRow.style.cssText = 'display: flex; align-items: center; gap: 8px; margin-bottom: 10px;';

    const scoreNum = document.createElement('span');
    scoreNum.style.cssText = `
      font-size: 24px; font-weight: 700; font-family: var(--font-mono);
      color: ${getCIIColor(ciiScore.overall)};
    `;
    scoreNum.textContent = String(ciiScore.overall);
    scoreRow.appendChild(scoreNum);

    const classification = getCIIClassification(ciiScore.overall);
    const classLabel = document.createElement('span');
    classLabel.style.cssText = `
      font-size: 10px; font-weight: 600; text-transform: uppercase;
      padding: 2px 6px; border-radius: 3px;
      background: ${getCIIColor(ciiScore.overall)}22;
      color: ${getCIIColor(ciiScore.overall)};
    `;
    classLabel.textContent = classification;
    scoreRow.appendChild(classLabel);

    // Trend arrow
    const trendArrow = document.createElement('span');
    trendArrow.style.cssText = 'font-size: 14px; margin-left: auto;';
    if (ciiScore.trend === 'rising') {
      trendArrow.textContent = '\u2191';
      trendArrow.style.color = 'var(--accent-red)';
      trendArrow.title = 'Rising';
    } else if (ciiScore.trend === 'falling') {
      trendArrow.textContent = '\u2193';
      trendArrow.style.color = 'var(--accent-green)';
      trendArrow.title = 'Falling';
    } else {
      trendArrow.textContent = '\u2192';
      trendArrow.style.color = 'var(--text-muted)';
      trendArrow.title = 'Stable';
    }
    scoreRow.appendChild(trendArrow);

    card.appendChild(scoreRow);

    // CII trend sparkline (from session-local history)
    const history = this.ciiHistory.get(countryCode.toUpperCase());
    if (history && history.length >= 2) {
      const sparkRow = document.createElement('div');
      sparkRow.style.cssText = 'margin-bottom: 10px; display: flex; align-items: center; gap: 6px;';

      const sparkLabel = document.createElement('span');
      sparkLabel.style.cssText = 'font-size: 9px; color: var(--text-muted);';
      sparkLabel.textContent = 'Trend';
      sparkRow.appendChild(sparkLabel);

      const sparkline = createTrendSparkline(history, {
        width: 120,
        height: 20,
        fill: true,
        referenceLine: history[0],
      });
      sparkRow.appendChild(sparkline);

      card.appendChild(sparkRow);
    }

    // Component bars
    const components: [string, number, string][] = [
      ['Unrest', ciiScore.components.unrest, '#f97316'],
      ['Conflict', ciiScore.components.conflict, '#ef4444'],
      ['Security', ciiScore.components.security, '#8b5cf6'],
      ['Information', ciiScore.components.information, '#06b6d4'],
    ];

    for (const [label, value, color] of components) {
      const row = document.createElement('div');
      row.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-bottom: 4px;';

      const labelEl = document.createElement('span');
      labelEl.style.cssText = 'font-size: 10px; color: var(--text-secondary); width: 70px; flex-shrink: 0;';
      labelEl.textContent = label;
      row.appendChild(labelEl);

      const barBg = document.createElement('div');
      barBg.style.cssText = `
        flex: 1; height: 6px; border-radius: 3px;
        background: var(--bg-primary);
      `;

      const barFill = document.createElement('div');
      barFill.style.cssText = `
        height: 100%; border-radius: 3px;
        width: ${(value / 25) * 100}%;
        background: ${color};
        transition: width 300ms ease;
      `;
      barBg.appendChild(barFill);
      row.appendChild(barBg);

      const valueEl = document.createElement('span');
      valueEl.style.cssText = 'font-size: 10px; font-family: var(--font-mono); color: var(--text-muted); width: 20px; text-align: right;';
      valueEl.textContent = String(value);
      row.appendChild(valueEl);

      card.appendChild(row);
    }

    // Last updated timestamp
    const updated = document.createElement('div');
    updated.style.cssText = 'font-size: 9px; color: var(--text-muted); margin-top: 8px; text-align: right;';
    const ts = new Date(ciiScore.lastUpdated);
    updated.textContent = `Updated ${ts.getUTCHours().toString().padStart(2, '0')}:${ts.getUTCMinutes().toString().padStart(2, '0')} UTC`;
    card.appendChild(updated);

    section.appendChild(card);
    return section;
  }

  /** Get all events matching a country code or country name. */
  private getCountryEvents(
    events: NormalizedEvent[],
    countryCode: string,
    countryName: string,
  ): NormalizedEvent[] {
    const code = countryCode.toUpperCase();
    const name = countryName.toUpperCase();
    return events.filter((e) => {
      const cc = e.location?.countryCode?.toUpperCase();
      if (cc === code) return true;
      const cn = e.location?.country?.toUpperCase();
      if (cn === name) return true;
      return false;
    });
  }

  /** Determine which active layers have data points in this country. */
  private getLayersWithData(countryEvents: NormalizedEvent[], activeLayers: string[]): string[] {
    const activeSet = new Set(activeLayers);
    const layersFound = new Set<string>();

    for (const ev of countryEvents) {
      // Check tags for layer IDs
      if (ev.tags) {
        for (const tag of ev.tags) {
          if (activeSet.has(tag)) {
            const def = getLayerById(tag);
            if (def) layersFound.add(def.name);
          }
        }
      }
      // Check type-based layer matches (derived from layer orchestrator)
      const mapped = TYPE_TO_LAYER[ev.type];
      if (mapped) {
        for (const layerId of mapped) {
          if (activeSet.has(layerId)) {
            const def = getLayerById(layerId);
            if (def) layersFound.add(def.name);
          }
        }
      }
    }

    return Array.from(layersFound).sort();
  }

}

/** Factory: create the country panel element. */
export function createCountryPanel(): HTMLElement {
  const panel = new CountryPanel();
  const el = panel.getElement();
  (el as HTMLElement & { destroy?: () => void }).destroy = () => panel.destroy();
  return el;
}
